import { useMemo } from "react";

import Box from "@mui/material/Box";
import { alpha, useTheme } from "@mui/material/styles";
import { withTranslation } from "react-i18next";
import { isEqual } from "lodash";

import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import {
  upstreamChain, downstreamChain, directedReach, bySourcing, sourcingText, reachTones,
  REACH_ARROW, REACH_DIR, DEPTH_ALL, DEFAULT_DEPTH,
} from "./distNetworkModel";
import { FS } from "@plannel/utils/font-util";

// 범위 목록은 상류(받아 오는 쪽)를 먼저 — 그래프를 위에서 아래로 읽는 순서와 같다.
const DIR_ORDER = { [REACH_DIR.UP]: 0, [REACH_DIR.DOWN]: 1 };

/*
  우측 인스펙터 — 선택한 위치 하나를 끝까지 설명한다.

  예전 화면의 우하단 두 그리드(Supplied / Supplying Items)는 위치를 고르기 전에는 빈 채로
  화면 절반을 차지했고, 골라도 "품목 목록"이라 정작 궁금한 "이 위치는 어디서 받아 어디로 보내나"는
  BOD 그리드를 따로 뒤져야 알 수 있었다. 여기서는 그 자리를 링크 관점으로 바꿨다 —
  상류 경로 한 줄, 받는 곳, 보내는 곳. 품목 단위 편집은 Table 뷰의 그리드가 계속 담당한다.
*/
const { locationType } = CodeUtil;

/*
  거점 인스펙터의 품목 목록 최대 높이(px).

  거점 하나가 수백 품목을 다루는 일이 흔하다. 목록을 그대로 늘어놓으면 그 아래의
  "N홉 안 / 받는 곳 / 보내는 곳"이 화면 밖으로 밀려나, 품목을 보러 들어왔다가
  나머지를 못 찾는다. 목록에 자체 스크롤을 주어 아래 섹션들이 늘 손 닿는 곳에 있게 한다.
  한 줄이 약 38px 이라 대략 일곱 줄이 보인다 — 훑기엔 충분하고 패널을 삼키지는 않는 높이.

  ※ 링크 뷰에는 적용하지 않는다. 거기서는 품목 목록이 유일한 내용이라
    남는 높이를 다 쓰는 편이 낫다(flex: 1).
*/
const ITEM_LIST_MAX_HEIGHT = 268;

const SECTION_LABEL_SX = {
  fontSize: FS.CAPTION, fontWeight: 600, color: "text.secondary", letterSpacing: "0.06em",
};

const DistributionNetworkInspector = ({
  t, network, selectedCd, depth = DEFAULT_DEPTH,
  selectedLink = null, selectedBundle = null,
  items = [], itemsLoading = false, itemsTruncated = false,
  flowItemCd = null, flowKeys = null, flowSourcing = null, onFollowItem, onSelect, onSelectLink,
}) => {
  const theme = useTheme();
  const node = selectedCd ? network.byCd.get(selectedCd) : null;

  /*
    품목을 따라가는 중이면 인스펙터도 그 품목 기준이다 — 그래프는 그 품목의 링크만 칠하는데
    인스펙터가 전체 품목 기준이면 둘이 다른 말을 한다(mj: MT_ENT_01 을 따라가며 서울점을 고르면
    그래프는 화성공장에서 오는데 공급 경로는 품목 수가 많은 구미공장을 가리켰다).
  */
  const flowing = flowKeys?.size > 0;

  /*
    공급 경로 한 줄 — 상류와 하류를 이어 붙인다.
    예전에는 상류만 보여 줘서 "이 DC 는 어디서 받나"는 알아도 "여기서 나간 게 어디까지 가나"는
    BOD 그리드를 따로 뒤져야 했다. 갈래가 여럿이면 주 공급선을 따라간다 — 평소엔 품목이 가장 많은 선,
    따라가는 중이면 그 품목의 조달 우선순위가 가장 앞선 선.
    (선택 노드가 양쪽에 한 번씩 들어가므로 상류 꼬리를 잘라 이어 붙인다)
  */
  const chain = useMemo(() => {
    if (!node) {
      return [];
    }
    const options = flowing ? { allowedKeys: flowKeys, compare: bySourcing(flowSourcing) } : undefined;
    const up = upstreamChain(network, node.locCd, options);
    const down = downstreamChain(network, node.locCd, options);
    return [...up.slice(0, -1), ...down];
  }, [network, node, flowing, flowKeys, flowSourcing]);

  // 받는 곳 / 보내는 곳 — 따라가는 중이면 그 품목이 실린 링크만, 주 공급처부터.
  const inboundLinks = useMemo(() => {
    if (!node) {
      return [];
    }
    return flowing
      ? node.inbound.filter(link => flowKeys.has(link.key)).sort(bySourcing(flowSourcing))
      : node.inbound;
  }, [node, flowing, flowKeys, flowSourcing]);
  const outboundLinks = useMemo(() => {
    if (!node) {
      return [];
    }
    return flowing ? node.outbound.filter(link => flowKeys.has(link.key)) : node.outbound;
  }, [node, flowing, flowKeys]);

  // 그래프와 같은 규칙으로 센다 — 고른 거점을 지나는 경로, 따라가는 중이면 그 품목의 링크로만.
  const reach = useMemo(
    () => directedReach(network, node?.locCd, depth, flowing ? flowKeys : null).nodes,
    [network, node, depth, flowing, flowKeys]
  );
  const tones = reachTones(flowing ? ColorUtil.distNetworkFlowColor() : theme.palette.primary.main);

  /*
    홉 목록은 depth 를 넓혔을 때만 낸다 — 1홉이면 아래 "받는 곳 / 보내는 곳" 과 같은 내용이라
    같은 목록을 두 번 보여 주는 꼴이 된다(따라가는 중이면 아래 두 목록도 그 품목 기준이라 마찬가지다).
    상류를 먼저, 가까운 순으로.
  */
  const hops = useMemo(() => (
    depth <= 1
      ? []
      : [...reach.entries()]
          .filter(([cd]) => cd !== selectedCd)
          .sort((a, b) => (
            DIR_ORDER[a[1].dir] - DIR_ORDER[b[1].dir] || a[1].hop - b[1].hop || (a[0] < b[0] ? -1 : 1)
          ))
  ), [reach, depth, selectedCd]);

  /*
    묶인 줄기를 고르면 어느 갈래인지부터 고르게 한다.
    허브에서 5갈래 이상 뻗으면 그림은 줄기 하나로 합쳐지는데(distNetworkModel 의 번들링),
    합쳐진 줄기는 눌러도 "어느 링크"인지 정할 수 없다. 그래서 갈래를 펼쳐 고르게 한 뒤
    아래의 링크 화면으로 넘어간다 — 갈래가 하나뿐인 줄기는 페이지가 바로 링크로 보낸다.
  */
  /*
    품목 목록 — 링크를 골랐을 때와 거점을 골랐을 때 같은 모양으로 쓴다.
    어느 쪽에서든 「따라가기」로 넘어갈 수 있어야 한다: 품목을 짚기 전에 "어느 링크인지"를
    먼저 정하라고 요구하면, 링크를 모르는 사람은 시작할 방법이 없다.
  */
  const renderItemRows = () => {
    if (itemsLoading) {
      return <Box sx={{ px: 1.75, py: 1, fontSize: FS.BODY, color: "text.disabled" }}>{t("loading")}</Box>;
    }
    if (!items.length) {
      return <Box sx={{ px: 1.75, py: 1, fontSize: FS.BODY, color: "text.disabled" }}>{t("MSG_NoItemOnLink")}</Box>;
    }

    return (
      <>
        {items.map(item => {
          const following = isEqual(flowItemCd, item.itemCd);
          return (
            <Box
              key={item.id ?? item.itemCd}
              sx={{
                display: "flex", alignItems: "center", gap: 1,
                px: 1, py: 0.75, borderRadius: 1,
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ fontSize: FS.SUB, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item.itemName || item.itemCd}
                </Box>
                {/* 코드와 계층명을 한 줄에 — 같은 이름의 품목이 계층별로 여러 개인 경우가 흔하다. */}
                <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {[item.itemCd, item.hrchyName].filter(Boolean).join(" · ")}
                </Box>
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => onFollowItem?.(following ? null : item.itemCd, item.itemName)}
                sx={{
                  flexShrink: 0, cursor: "pointer", font: "inherit",
                  fontSize: FS.CAPTION, lineHeight: 1.6, px: 0.875, py: "1px",
                  borderRadius: 999, border: 1,
                  borderColor: following ? ColorUtil.distNetworkFlowColor() : "divider",
                  bgcolor: following ? ColorUtil.distNetworkFlowColor() : "background.paper",
                  color: following ? "common.white" : "text.secondary",
                  "&:hover": {
                    borderColor: ColorUtil.distNetworkFlowColor(),
                    color: following ? "common.white" : ColorUtil.distNetworkFlowColor(),
                  },
                }}
              >
                {following ? t("following") : t("followItem")}
              </Box>
            </Box>
          );
        })}
        {/* 허브는 서버에서 잘라 온다 — 잘렸다는 사실을 숨기지 않고, 좁히는 방법을 함께 알려 준다. */}
        {itemsTruncated && (
          <Box sx={{ px: 1, py: 0.75, fontSize: FS.CAPTION, color: "text.disabled" }}>
            {/* 보간 변수는 count 가 아니다 — count 를 쓰면 i18next 가 복수형 키(_one/_other)를 먼저 찾는다 */}
            {t("MSG_ItemListTruncated", { shown: items.length })}
          </Box>
        )}
      </>
    );
  };

  if (selectedBundle) {
    const from = network.byCd.get(selectedBundle.from);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          <Box sx={SECTION_LABEL_SX}>{t("bundledLinks")}</Box>
          <Box sx={{ fontSize: FS.TITLE, fontWeight: 600, mt: 0.5, overflowWrap: "anywhere" }}>
            {`${from?.name ?? selectedBundle.from} → ${selectedBundle.bandKey || t("none")}`}
          </Box>
          <Box sx={{ fontSize: FS.CAPTION, color: "text.secondary", mt: 0.25 }}>
            {t("MSG_PickLinkFromBundle")}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 0.75, py: 1 }}>
          {selectedBundle.links.map(link => {
            const other = network.byCd.get(link.to);
            return (
              <Box
                key={link.key}
                onClick={() => onSelectLink?.(link)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  px: 1, py: 0.75, borderRadius: 1, cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Box sx={{
                  width: 3, height: 20, borderRadius: 999, flexShrink: 0,
                  bgcolor: ColorUtil.locationTypeColor(other?.locType),
                }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ fontSize: FS.SUB, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {other?.name ?? link.to}
                  </Box>
                  <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>{other?.locCd ?? link.to}</Box>
                </Box>
                <Box sx={{ fontSize: FS.BODY, fontWeight: 600, color: "text.secondary", flexShrink: 0 }}>{link.items}</Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  /*
    링크를 고르면 그 구간을 타고 흐르는 품목을 낸다.
    이 화면은 원래 "어디가 어디에 연결됐나"만 답했다 — 정작 "이 구간으로 무엇이 가나"는
    Table 뷰의 그리드도 거점 기준이라 답하지 못한다. 여기서 그 자리를 메운다.
  */
  if (selectedLink) {
    const from = network.byCd.get(selectedLink.from);
    const to = network.byCd.get(selectedLink.to);

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          <Box sx={SECTION_LABEL_SX}>{t("selectedLink")}</Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5, flexWrap: "wrap" }}>
            <Box
              onClick={() => onSelect(selectedLink.from)}
              sx={{ fontSize: FS.TITLE, fontWeight: 600, cursor: "pointer", "&:hover": { color: "primary.main" } }}
            >
              {from?.name ?? selectedLink.from}
            </Box>
            <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>→</Box>
            <Box
              onClick={() => onSelect(selectedLink.to)}
              sx={{ fontSize: FS.TITLE, fontWeight: 600, cursor: "pointer", "&:hover": { color: "primary.main" } }}
            >
              {to?.name ?? selectedLink.to}
            </Box>
          </Box>
          <Box sx={{ fontSize: FS.CAPTION, color: "text.secondary", mt: 0.25 }}>
            {`${selectedLink.from} → ${selectedLink.to}`}
            {selectedLink.avgLtDays != null ? ` · ${t("avgLtDays")} ${selectedLink.avgLtDays}` : ""}
          </Box>
        </Box>

        <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, display: "flex", alignItems: "baseline", flexShrink: 0 }}>
          <Box sx={SECTION_LABEL_SX}>{t("itemsOnLink")}</Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>
            {itemsLoading ? "…" : items.length}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 0.75, pb: 1 }}>
          {renderItemRows()}
        </Box>
      </Box>
    );
  }

  if (!node) {
    return (
      <Box sx={{
        height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
        px: 3, textAlign: "center", fontSize: FS.SUB, color: "text.disabled",
      }}>
        {t("MSG_SelectLocationOrLink")}
      </Box>
    );
  }

  const typeName = locationType?.[node.locType]
    ? GridUtils.gridValueL10N(locationType[node.locType].name)
    : (node.locType || t("none"));
  const typeColor = ColorUtil.locationTypeColor(node.locType);

  /*
    빈 목록 문구. 따라가는 품목이 이 거점을 아예 지나지 않으면 "공급 출발점입니다 / 사슬의 끝입니다"는
    틀린 말이다 — 거점이 그런 게 아니라 품목이 여기 안 온다. 한쪽만 비었으면 원래 문구가 맞다
    (품목이 공장에서 시작하거나 매장에서 끝난다).
  */
  const notOnItemPath = flowing && !inboundLinks.length && !outboundLinks.length;
  const emptyInbound = notOnItemPath ? t("MSG_ItemNotThroughLocation") : t("MSG_NoInboundLink");
  const emptyOutbound = notOnItemPath ? t("MSG_ItemNotThroughLocation") : t("MSG_NoOutboundLink");

  // 따라가는 중이면 그 품목의 링크 수만 센다. "내보내는 품목 수"는 품목 하나로는 뜻이 없어 뺀다.
  const stats = [
    { label: t("inbound"), value: inboundLinks.length },
    { label: t("outbound"), value: outboundLinks.length },
    ...(flowing ? [] : [{ label: t("itemsOut"), value: node.itemsOut || "—" }]),
  ];


  const renderLinkRows = (links, pick, emptyMessage) => (
    <Box sx={{ px: 0.75, pb: 1 }}>
      {links.map(link => {
        const other = network.byCd.get(pick(link));
        return (
          <Box
            key={link.key}
            onClick={() => onSelect(other?.locCd)}
            sx={{
              display: "flex", alignItems: "center", gap: 1,
              px: 1, py: 0.75, borderRadius: 1, cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box sx={{
              width: 3, height: 20, borderRadius: 999, flexShrink: 0,
              bgcolor: ColorUtil.locationTypeColor(other?.locType),
            }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ fontSize: FS.SUB, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {other?.name ?? pick(link)}
              </Box>
              <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>{other?.locCd ?? pick(link)}</Box>
            </Box>
            {/* 평소엔 이 링크의 품목 수, 따라가는 중이면 그 품목의 우선순위·비율 */}
            <Box sx={{
              fontSize: FS.BODY, fontWeight: 600, flexShrink: 0,
              color: flowing ? ColorUtil.distNetworkFlowColor() : "text.secondary",
            }}>
              {flowing ? (sourcingText(flowSourcing?.get(link.id)) || "—") : link.items}
            </Box>
          </Box>
        );
      })}
      {!links.length && (
        <Box sx={{ px: 1, py: 1, fontSize: FS.BODY, color: "text.disabled" }}>{emptyMessage}</Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={SECTION_LABEL_SX}>{t("selected")}</Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{
            ...ColorUtil.tintChipSx(typeColor),
            fontSize: FS.CAPTION, lineHeight: 1, px: 0.75, py: "4px", borderRadius: 0.75, whiteSpace: "nowrap",
          }}>
            {typeName}
          </Box>
        </Box>
        <Box sx={{ fontSize: FS.H1, fontWeight: 600, mt: 0.5, overflowWrap: "anywhere" }}>{node.name}</Box>
        <Box sx={{ fontSize: FS.CAPTION, color: "text.secondary", mt: 0.25 }}>
          {[node.locCd, node.siteCd, node.siteName].filter(Boolean).join(" · ")}
        </Box>
      </Box>

      <Box sx={{ display: "flex", borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        {stats.map((stat, idx) => (
          <Box key={stat.label} sx={{
            flex: 1, px: 1.5, py: 1,
            borderRight: idx < stats.length - 1 ? 1 : 0, borderColor: "divider",
          }}>
            <Box sx={{ fontSize: FS.H1, fontWeight: 600, lineHeight: 1.2 }}>{stat.value}</Box>
            <Box sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{stat.label}</Box>
          </Box>
        ))}
      </Box>

      {/* 공급 경로 — 갈래가 여럿이면 주 공급선을 따라간 한 줄이다(상류 → 하류). 따라가는 중이면 그 품목 기준. */}
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Box sx={SECTION_LABEL_SX}>{t("supplyPath")}</Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.625, mt: 0.75 }}>
          {/* key 에 인덱스를 섞는다 — 순환(A→B→A)이면 상류·하류가 같은 코드를 한 번씩 담아 겹친다 */}
          {chain.map((cd, idx) => (
            <Box key={`${cd}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
              <Box
                onClick={() => onSelect(cd)}
                sx={{
                  fontSize: FS.BODY, px: 0.875, py: "4px", borderRadius: 0.75, cursor: "pointer",
                  bgcolor: cd === node.locCd ? "primary.main" : theme => alpha(theme.palette.primary.main, 0.08),
                  color: cd === node.locCd ? "primary.contrastText" : "text.primary",
                  fontWeight: cd === node.locCd ? 600 : 400,
                }}
              >
                {network.byCd.get(cd)?.name ?? cd}
              </Box>
              {idx < chain.length - 1 && <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>→</Box>}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {/*
          이 거점을 드나드는 품목. 링크를 고르지 않고도 여기서 바로 「따라가기」로 넘어갈 수 있다 —
          어느 링크인지 먼저 정하라고 요구하면, 링크를 모르는 사람은 시작할 방법이 없다.
          링크 하나로 좁혀 보고 싶으면 아래 「받는 곳 / 보내는 곳」이나 그래프의 선을 누르면 된다.
        */}
        <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, display: "flex", alignItems: "baseline" }}>
          <Box sx={SECTION_LABEL_SX}>{t("itemsAtLocation")}</Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>
            {itemsLoading ? "…" : items.length}
          </Box>
        </Box>
        <Box sx={{ px: 0.75, pb: 1, maxHeight: ITEM_LIST_MAX_HEIGHT, overflow: "auto" }}>
          {renderItemRows()}
        </Box>

        {/* 강조 범위 안에 든 위치들. 1홉일 때는 아래 두 목록과 같은 내용이라 내지 않는다. */}
        {hops.length > 0 && (
          <>
            <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, display: "flex", alignItems: "baseline" }}>
              <Box sx={SECTION_LABEL_SX}>
                {/* 보간 변수는 count 가 아니다 — count 를 쓰면 i18next 가 복수형 키(_one/_other)를 먼저 찾는다 */}
                {depth >= DEPTH_ALL ? t("allConnected") : t("withinHops", { hops: depth })}
              </Box>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>{hops.length}</Box>
            </Box>
            <Box sx={{ px: 0.75, pb: 1 }}>
              {hops.map(([cd, { hop, dir }]) => {
                const other = network.byCd.get(cd);
                return (
                  <Box
                    key={cd}
                    onClick={() => onSelect(cd)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1,
                      px: 1, py: 0.5, borderRadius: 1, cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Box sx={{
                      fontSize: FS.CAPTION, fontWeight: 600, flexShrink: 0,
                      minWidth: 26, textAlign: "center", borderRadius: 0.75, px: 0.5,
                      color: tones[dir], bgcolor: alpha(tones[dir], 0.1),
                    }}>
                      {`${REACH_ARROW[dir]}${hop}`}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1, fontSize: FS.SUB, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {other?.name ?? cd}
                    </Box>
                    <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled", flexShrink: 0 }}>{cd}</Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}

        <Box sx={{
          px: 1.5, pt: 1.25, pb: 0.5, display: "flex", alignItems: "baseline",
          ...(hops.length > 0 ? { borderTop: 1, borderColor: "divider", pt: 1 } : null),
        }}>
          <Box sx={SECTION_LABEL_SX}>{t("suppliedBy")}</Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>{flowing ? t("priority") : t("items")}</Box>
        </Box>
        {renderLinkRows(inboundLinks, link => link.from, emptyInbound)}

        <Box sx={{
          px: 1.5, pt: 1, pb: 0.5, borderTop: 1, borderColor: "divider",
          display: "flex", alignItems: "baseline",
        }}>
          <Box sx={SECTION_LABEL_SX}>{t("suppliesTo")}</Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ fontSize: FS.CAPTION, color: "text.disabled" }}>{flowing ? t("priority") : t("items")}</Box>
        </Box>
        {renderLinkRows(outboundLinks, link => link.to, emptyOutbound)}
      </Box>
    </Box>
  );
};

export default withTranslation()(DistributionNetworkInspector);
